import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { IAuthFeatureState } from './auth.states';
import {
  initAuth,
  authenticatedProfileFailed,
  authenticatedProfileReady,
  loadAuth,
  login,
  loginFailed,
  loginSuccess,
  lineLogin,
  lineLoginFailed,
  logoutFailure,
  logoutSuccess,
  setIsFirstTime,
  storedAuth,
  updateRoles,
  updateToken,
} from './auth.action';
import { catchError, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { selectStoredAuth } from './auth.selectors';
import { AuthService } from 'src/app/services/auth.service';
import { setUserInfo } from '../user/user.actions';
import { UserService } from 'src/app/services/user.service';
import { Router } from '@angular/router';
import { HelperService } from 'src/app/services/helper.service';
import { AdminRole, MerchantRole } from 'src/app/shared/enums/roles';
import { IAuth } from './auth.states';
import { IUserProfile } from '../user/user.states';
import { IResponse } from 'src/app/shared/interfaces/utils.interface';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private store: Store<{ auth: IAuthFeatureState }>,
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private helperService: HelperService,
  ) {}

  loadAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initAuth),
      switchMap(() => {
        const storedAuth = localStorage.getItem('auth');

        if (storedAuth) {
          const auth = JSON.parse(storedAuth);
          if (auth.accessToken && auth.refreshToken) {
            return of(loadAuth({ currentUser: auth }));
          }
        }
        return of(loadAuth({ currentUser: null }));
      }),
    ),
  );
  storedAuth$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          setIsFirstTime,
          authenticatedProfileFailed,
          loginSuccess,
          logoutSuccess,
          updateToken,
          updateRoles,
        ),
        withLatestFrom(this.store.select(selectStoredAuth)),
        tap(([action, auth]) => {
          if (auth) {
            localStorage.setItem('auth', JSON.stringify(auth));
          } else {
            localStorage.removeItem('auth');
          }
        }),
      ),
    { dispatch: false },
  );

  loginEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      switchMap((action) => {
        return this.authService.login(action.login).pipe(
          switchMap((item: any) => [
            setUserInfo({ user: null }),
            loginSuccess({ currentUser: item.data }),
            // storedAuth(),
            // UserActions.loadUserInfo({user: item})
          ]),
          catchError((error: any) =>
            of(loginFailed({ error: error.statusMessage })),
          ),
        );
      }),
    ),
  );

  lineLoginEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lineLogin),
      switchMap(({ payload }) =>
        this.authService.lineLogin(payload).pipe(
          switchMap((item: any) => {
            const currentUser = {
              ...item.data,
              authSource: 'LINE' as const,
            };
            return [setUserInfo({ user: null }), loginSuccess({ currentUser })];
          }),
          catchError((error: any) =>
            of(
              lineLoginFailed({
                error:
                  error?.statusMessage ??
                  'Unable to sign in with LINE. Please try again.',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  initializeAuthenticatedProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginSuccess),
      switchMap(({ currentUser }) => {
        if (this.isFreeOrganizer(currentUser)) {
          return of(authenticatedProfileReady({ currentUser, profile: null }));
        }

        return this.userService.initMerchant().pipe(
          map((response: IResponse<IUserProfile>) => {
            const profile = this.normalizeProfile(response.data);
            if (profile._id !== currentUser._id) {
              throw new Error('Authenticated merchant profile mismatch.');
            }
            return authenticatedProfileReady({ currentUser, profile });
          }),
          catchError((error) =>
            of(
              authenticatedProfileFailed({
                error:
                  error?.statusMessage ??
                  error?.message ??
                  'Unable to initialize the merchant profile.',
              }),
            ),
          ),
        );
      }),
    ),
  );

  navigateAfterProfileInitialization$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authenticatedProfileReady),
        tap(({ currentUser, profile }) => {
          const route = this.authenticatedRoute(currentUser, profile);
          if (route) this.router.navigate(route);
        }),
      ),
    { dispatch: false },
  );

  navigateAfterProfileFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authenticatedProfileFailed),
        tap(() => this.router.navigate(['/login'])),
      ),
    { dispatch: false },
  );

  private isFreeOrganizer(user: IAuth): boolean {
    return (
      user.canAccessFreeOrganizer === true || (user.roles as string) === 'USER'
    );
  }

  private normalizeProfile(profile: IUserProfile): IUserProfile {
    const merchant = profile.merchant;
    if (!merchant || merchant.sportType.length !== 1) return profile;

    const normalizedSportTypes = merchant.sportType[0].split(',');
    if (normalizedSportTypes.length === 1) return profile;

    return {
      ...profile,
      merchant: {
        ...merchant,
        sportType: normalizedSportTypes,
      },
    };
  }

  private authenticatedRoute(
    currentUser: IAuth,
    profile: IUserProfile | null,
  ): string[] | null {
    if (this.isFreeOrganizer(currentUser)) {
      return ['/free-organizer/home'];
    }
    if (currentUser.isFirstTime) {
      return ['/change-password'];
    }
    if (
      profile?.isInitMerchant &&
      this.helperService.isMerchant(currentUser.roles)
    ) {
      return ['/merchant'];
    }
    if (!profile?.isInitMerchant && currentUser.roles === MerchantRole.ADMIN) {
      return ['/setup/merchant'];
    }
    if (this.helperService.isMerchant(currentUser.roles)) {
      return ['/merchant'];
    }
    if (
      currentUser.roles === AdminRole.BACKOFFICE_ADMIN ||
      currentUser.roles === AdminRole.SUPERADMIN
    ) {
      return ['/admin'];
    }
    return null;
  }

  // logout$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(logoutRequest),
  //     // exhaustMap((action) => this.userService.logout()),
  //     switchMap((action) => {
  //       return this.authService.logout().pipe(
  //         switchMap((item: any) => [
  //           // AuthUserActions.loginSuccess({item}),
  //           logoutSuccess(),
  //           setUserInfo({ user: null }),
  //         ]),
  //         catchError((error: any) =>
  //           of(logoutFailure({ error: error.statusMessage }))
  //         )
  //       );
  //     })
  //   )
  // );

  // logoutSuccess$ = createEffect(
  //   () =>
  //     this.actions$.pipe(
  //       ofType(logoutSuccess),
  //       tap((item: any) => {
  //         localStorage.removeItem('profile');
  //         localStorage.removeItem('auth');
  //         this.router.navigate(['']);
  //       })
  //     ),
  //   { dispatch: false }
  // );
}
